/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IOrderTemplateNameDialogResources, OrderTemplateNameDialog } from '@msdyn365-commerce/components';
import { IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { OrderTemplate, Random } from '@msdyn365-commerce-modules/retail-actions';
import { Button, ITelemetryContent, Popover } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useState } from 'react';

import { DeleteOrderTemplateDialog, IDeleteOrderTemplateDialogResources } from './order-template-delete-modal';
import { ShareOrderTemplateDialog, IShareOrderTemplateDialogResources } from './order-template-share-modal';

export interface IActionsSplitButtonData {
    orderTemplate: OrderTemplate;
}

export interface IActionsSplitButtonProps extends IComponentProps<IActionsSplitButtonData> {
    context: ICoreContext;
    telemetryContent?: ITelemetryContent;
    handlers: {
        onRemoveTemplate(orderTemplate: ProductList | string): Promise<void> | void;
        onRenameTemplate(orderTemplate: ProductList): Promise<void>;
        onShareTemplate?(orderTemplate: ProductList): Promise<void>;
    };
    renameOrderTemplateDialogStrings: IOrderTemplateNameDialogResources;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    isShared?: boolean;
    canManageOrderTemplates?: boolean;
    showOptionsLabel?: boolean;
    isB2bContact?: boolean;
    resources: {
        renameTemplateButtonText: string;
        deleteTemplateButtonText: string;
        templateShareButtonText?: string;
        templateUnshareButtonText?: string;
        ShareOrderTemplateDialog?: IShareOrderTemplateDialogResources;
        DeleteOrderTemplateDialog?: IDeleteOrderTemplateDialogResources;
    };
}

const canManageOrderTemplate = (
    productList: ProductList,
    context: ICoreContext,
    isShared?: boolean,
    canManageOrderTemplates?: boolean
): boolean => {
    // Keep in sync with order-template-actions.tsx::canManageOrderTemplate
    const isTemplateShared = Boolean(isShared || productList.IsShared);
    const userContactId = context?.request?.user?.contactPersonId;
    const isB2bContact = context?.request?.user?.isB2bContact;
    const isB2bContactOwner = !!productList.ContactPersonId && !!userContactId && productList.ContactPersonId === userContactId;
    const isTemplateOwner = !isB2bContact || isB2bContactOwner;

    return isTemplateOwner || (isTemplateShared && !!canManageOrderTemplates);
};

export const ActionsSplitButton: React.FC<IActionsSplitButtonProps> = (props: IActionsSplitButtonProps) => {
    const [isStatementRequestAdminDropdownOpen, setIsStatementRequestAdminDropdownOpen] = useState(false);
    const [renameOrderTemplateDialogOpen, setOrderTemplateNameDialogOpen] = React.useState(false);
    const [isShareOrderTemplateDialogOpen, setShareOrderTemplateDialogOpen] = React.useState(false);
    const [isDeleteOrderTemplateDialogOpen, setDeleteOrderTemplateDialogOpen] = React.useState(false);

    const isShared = props.isShared ?? false;
    const showOptionsLabel = props.showOptionsLabel ?? false;

    const buttonEventReference = React.useRef<HTMLButtonElement | null>(null);

    const onOrderTemplateNameCancel = () => {
        context.telemetry.debug('Rename of template was canceled.');
        buttonEventReference.current?.focus();
    };

    const onRenameButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setOrderTemplateNameDialogOpen(true);
        event.preventDefault();
    };

    const onShareButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setShareOrderTemplateDialogOpen(true);
        event.preventDefault();
    };

    const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        buttonEventReference.current = event.target as HTMLButtonElement;
        setDeleteOrderTemplateDialogOpen(true);
        event.preventDefault();
    };

    const onOrderTemplateDeleteCancel = () => {
        context.telemetry.debug('Delete of template was canceled.');
        buttonEventReference.current?.focus();
    };

    const onToggleDropdown1 = () => {
        setIsStatementRequestAdminDropdownOpen(!isStatementRequestAdminDropdownOpen);
    };

    const {
        data: { orderTemplate },
        resources,
        renameOrderTemplateDialogStrings,
        context
    } = props;

    const className = 'ms-order-template-action-bar';

    const popoverReference = React.createRef<HTMLButtonElement>();
    const renderModalPopup = (
        <OrderTemplateNameDialog
            existingOrderTemplate={orderTemplate.productList}
            isUpdateDialog
            className='msc-order-template-name-dialog'
            modalOpen={renameOrderTemplateDialogOpen}
            setModalOpen={setOrderTemplateNameDialogOpen}
            context={context}
            id={props.id}
            data={props.data}
            orderTemplateName={props.data.orderTemplate.productList.Name}
            typeName={props.typeName}
            dialogStrings={renameOrderTemplateDialogStrings}
            onCancel={onOrderTemplateNameCancel}
            onSuccess={props.handlers.onRenameTemplate}
        />
    );
    const requestStatementDropdownString = `request-statement-dropdown-id-${Random.Guid.generateGuid()}`;

    const renderButton = canManageOrderTemplate(orderTemplate.productList, props.context, props.isShared, props.canManageOrderTemplates);

    return renderButton ? (
        <div className={`${className}__dropdown`}>
            {renderModalPopup}
            {resources.ShareOrderTemplateDialog && props.handlers.onShareTemplate && (
                <ShareOrderTemplateDialog
                    className='msc-share-order-template-dialog'
                    modalOpen={isShareOrderTemplateDialogOpen}
                    setModalOpen={setShareOrderTemplateDialogOpen}
                    isShared={isShared}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onShareTemplate}
                    resources={resources.ShareOrderTemplateDialog}
                />
            )}
            {resources.DeleteOrderTemplateDialog && props.handlers.onRemoveTemplate && (
                <DeleteOrderTemplateDialog
                    modalOpen={isDeleteOrderTemplateDialogOpen}
                    setModalOpen={setDeleteOrderTemplateDialogOpen}
                    productList={orderTemplate.productList}
                    context={context}
                    id={props.id}
                    data={props.data}
                    typeName={props.typeName}
                    onSuccess={props.handlers.onRemoveTemplate}
                    onCancel={onOrderTemplateDeleteCancel}
                    resources={resources.DeleteOrderTemplateDialog}
                />
            )}
            {showOptionsLabel ? (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? `${className}__action__requestStatement__toggleUp`
                            : `${className}__action__requestStatement__toggleDown`
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                    id={requestStatementDropdownString}
                >
                    <span className={`${className}__action__requestStatement__buttonLabel`}>{'Options'}</span>
                </Button>
            ) : (
                <Button
                    className={
                        isStatementRequestAdminDropdownOpen
                            ? 'ms-order-template__list-options-toggle ms-order-template__list-options-toggle--open'
                            : 'ms-order-template__list-options-toggle'
                    }
                    onClick={onToggleDropdown1}
                    innerRef={popoverReference}
                    aria-haspopup='true'
                    aria-expanded={isStatementRequestAdminDropdownOpen}
                />
            )}
            <Popover
                aria-labelledby={requestStatementDropdownString}
                className={`${className}__action__requestStatement__dropdownVisible`}
                isOpen={isStatementRequestAdminDropdownOpen}
                placement='bottom'
                target={popoverReference}
                toggle={onToggleDropdown1}
                role='listbox'
                aria-hidden={!isStatementRequestAdminDropdownOpen}
            >
                <Button className={`${className}__action__requestStatement__dropdownOption rename`} onClick={onRenameButtonClick}>
                    {resources.renameTemplateButtonText}
                </Button>

                {(props.isB2bContact ?? false) && (props.canManageOrderTemplates ?? false) && (
                    <Button
                        className={`${className}__action__requestStatement__dropdownOption share`}
                        onClick={onShareButtonClick}
                        role='option'
                    >
                        {isShared ? resources.templateUnshareButtonText : resources.templateShareButtonText}
                    </Button>
                )}

                <Button
                    className={`${className}__action__requestStatement__dropdownOption delete`}
                    onClick={onDeleteButtonClick}
                    role='option'
                >
                    {resources.deleteTemplateButtonText}
                </Button>
            </Popover>
        </div>
    ) : null;
};
